import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { userService, type User } from '@/services/userService'
import { companyService, type Company } from '@/services/companyService'
import { authService } from '@/services/authService'
import { useLanguage } from '@/contexts/LanguageContext'
import { useNotifications } from '@/contexts/NotificationContext'
import { logError, responseErrorMessage, toUserMessage } from '@/utils/errorMessage'

interface AddUserData {
  first_name: string
  last_name: string
  email: string
  password: string
  role: 'admin' | 'operator' | 'superadmin' | 'customer'
  company_id: number
}

interface EditUserData {
  first_name: string
  last_name: string
  email: string
  role: 'admin' | 'operator' | 'superadmin' | 'customer'
  company_id: number
}

/**
 * HATA MODELI -- neden tek `error` yerine 5 ayri state:
 *
 * Eskiden tek bir `error` state'i vardi ve tek banner sayfa govdesindeydi
 * (UserManagementScreen icinde, modal backdrop'unun ALTINDA). Sonuc: 4 modalin
 * hicbiri hata gosteremiyordu; "Sifreler eslesmiyor" ya da e-posta duplicate
 * hatasi bile kullaniciya ulasmiyordu.
 *
 * Secim: MODAL BASINA AYRI STATE (`addUserError` / `editUserError` /
 * `deleteUserError` / `passwordError`) + sayfa govdesi icin ayri `loadError`.
 * Boylece:
 *   - her hata dogdugu yuzeyde gosterilir (modaldaki hata modalin icinde),
 *   - bir modaldaki hata baska bir modal acilinca "hayalet" olarak gorunmez,
 *   - liste yukleme hatasi modal kapandiktan sonra da sayfada asili kalir.
 * Modal ACILIRKEN ilgili hata temizlenir (open* fonksiyonlari).
 *
 * `companiesError` sirket listesi cekilemedigi durumdur: Ekle/Duzenle
 * modallarinda sirket dropdown'u bos kalacagi icin o modallarin hata slotunda
 * da gosterilir, ayrica acilista toast ile duyurulur.
 */
export const useUserManagementViewModel = () => {
  const navigate = useNavigate()
  const { t } = useLanguage()
  const { notifyError } = useNotifications()

  const [users, setUsers] = useState<User[]>([])
  const [companies, setCompanies] = useState<Company[]>([])
  const [loading, setLoading] = useState(true)
  /** Sayfa govdesi hatasi: yalnizca liste/oturum yukleme sorunlari. */
  const [loadError, setLoadError] = useState<string | null>(null)
  /** Sirket listesi cekilemedi -- Ekle/Duzenle modallarinda da gosterilir. */
  const [companiesError, setCompaniesError] = useState<string | null>(null)
  /** Modal basina hata (bkz. yukaridaki HATA MODELI notu). */
  const [addUserError, setAddUserError] = useState<string | null>(null)
  const [editUserError, setEditUserError] = useState<string | null>(null)
  const [deleteUserError, setDeleteUserError] = useState<string | null>(null)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  /** Modallarin `isSubmitting` prop'u: cift gonderimi ve erken kapanmayi engeller. */
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [currentUser, setCurrentUser] = useState<any>(null)

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [deletingUser, setDeletingUser] = useState<User | null>(null)
  const [passwordUser, setPasswordUser] = useState<User | null>(null)

  // Dropdown states
  const [showRoleDropdown, setShowRoleDropdown] = useState(false)
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false)
  const [showEditRoleDropdown, setShowEditRoleDropdown] = useState(false)
  const [showEditCompanyDropdown, setShowEditCompanyDropdown] = useState(false)

  // Form states
  const [newUser, setNewUser] = useState<AddUserData>({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    role: 'operator',
    company_id: 0
  })

  const [editUser, setEditUser] = useState<EditUserData>({
    first_name: '',
    last_name: '',
    email: '',
    role: 'operator',
    company_id: 0
  })

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')


  // Load current user
  useEffect(() => {
    const loadCurrentUser = () => {
      try {
        const userData = localStorage.getItem('deepwind-user')
        if (userData) {
          const user = JSON.parse(userData)
          setCurrentUser(user)

          // Restrict access - only superadmin and admin can access user management
          if (user.role !== 'superadmin' && user.role !== 'admin') {
            navigate('/dashboard', { replace: true })
            return
          }
        } else {
          // Oturum anahtari hic yok. else'siz birakildiginda currentUser null
          // kaliyor, loadUsers hic calismiyor ve tablo sonsuza kadar
          // "yukleniyor" gorunuyordu -- catch dalindakiyle ayni sonuc.
          setLoading(false)
          setLoadError(t.errors.sessionExpired)
          notifyError(t.errors.title, t.errors.sessionExpired)
        }
      } catch (error) {
        // Bozuk oturum verisi: currentUser null kalir, loadUsers hic calismaz ve
        // tablo sonsuza kadar "yukleniyor" gorunurdu. Spinner'i durdurup hatayi
        // hem sayfa banner'inda hem toast ile yuzeye cikariyoruz.
        logError('UserManagement.loadCurrentUser', error)
        setLoading(false)
        setLoadError(t.errors.sessionExpired)
        notifyError(t.errors.title, t.errors.sessionExpired)
      }
    }

    loadCurrentUser()
  }, [navigate])

  // Load companies
  const loadCompanies = useCallback(async () => {
    try {
      const response = await companyService.getCompanies()
      if (response.success && Array.isArray(response.data)) {
        setCompanies(response.data)
        setCompaniesError(null)
      } else {
        // Eskiden bu dal hic yoktu: sirket listesi bos geliyor, kullanici
        // "Sirket Secin" dropdown'unun neden bos oldugunu bilemiyordu.
        // TODO(i18n): strings.ts'e dokunulmadigi icin baglamsal metin TR sabit.
        const message = responseErrorMessage(response, t, 'Şirket listesi yüklenemedi')
        setCompaniesError(message)
        notifyError(t.errors.title, message)
      }
    } catch (error) {
      logError('UserManagement.loadCompanies', error)
      // TODO(i18n)
      const message = toUserMessage(error, t, 'Şirket listesi yüklenemedi')
      setCompaniesError(message)
      notifyError(t.errors.title, message)
    }
  }, [t, notifyError])

  // Load users from API
  const loadUsers = useCallback(async () => {
    if (!currentUser) return

    try {
      setLoading(true)
      setLoadError(null)

      // Superadmin sees all users, others see only their company users
      const response = await userService.getUsers(
        currentUser.id,
        currentUser.role,
        currentUser.role === 'superadmin' ? undefined : currentUser.company_id
      )

      if (response.success) {
        setUsers(response.data)
      } else {
        // Mevcut liste KORUNUR (rule: hata != bos veri); banner + "Tekrar dene".
        // TODO(i18n)
        setLoadError(responseErrorMessage(response, t, 'Kullanıcılar yüklenirken hata oluştu'))
      }
    } catch (error) {
      logError('UserManagement.loadUsers', error)
      // TODO(i18n)
      setLoadError(toUserMessage(error, t, 'Kullanıcılar yüklenirken hata oluştu'))
    } finally {
      setLoading(false)
    }
  }, [currentUser, t])

  // --- Modal acma/kapama: acilista ilgili hata slotu temizlenir ---
  const openAddModal = useCallback(() => {
    setAddUserError(null)
    setShowAddModal(true)
  }, [])

  const closeAddModal = useCallback(() => {
    setShowAddModal(false)
    setShowRoleDropdown(false)
    setShowCompanyDropdown(false)
    setAddUserError(null)
  }, [])

  const closeEditModal = useCallback(() => {
    setShowEditModal(false)
    setShowEditRoleDropdown(false)
    setShowEditCompanyDropdown(false)
    setEditUserError(null)
  }, [])

  // NOT: `deletingUser` / `passwordUser` kapanista BILEREK null'lanmiyor.
  // Modal'in cikis animasyonu sirasinda govde hala render edilir; null yaparsak
  // kullanici adi bir an bos gorunur. Ilgili kayit modal her acilista
  // (handleDeleteUser / handleChangePassword) yeniden yaziliyor.
  const closeDeleteModal = useCallback(() => {
    setShowDeleteModal(false)
    setDeleteUserError(null)
  }, [])

  const closePasswordModal = useCallback(() => {
    setShowPasswordModal(false)
    setPasswordError(null)
  }, [])

  // Add user
  const handleAddUser = async () => {
    if (isSubmitting) return

    // Validasyon mesajlari da modalin ICINDEKI slota gider.
    // TODO(i18n): validasyon metinleri strings.ts'te yok, TR sabit birakildi.
    if (!newUser.first_name.trim() || !newUser.last_name.trim() || !newUser.email.trim() || !newUser.password.trim()) {
      setAddUserError('Tüm alanları doldurun')
      return
    }

    if (newUser.company_id === 0) {
      setAddUserError('Şirket seçin')
      return
    }

    try {
      setIsSubmitting(true)
      setLoading(true)
      setAddUserError(null)

      const userDataWithDefaults = {
        ...newUser,
        is_active: true
      };
      const response = await userService.addUser(userDataWithDefaults)

      if (response.success) {
        await loadUsers()
        setShowAddModal(false)
        setShowRoleDropdown(false)
        setShowCompanyDropdown(false)
        setNewUser({
          first_name: '',
          last_name: '',
          email: '',
          password: '',
          role: 'operator',
          company_id: 0
        })
      } else {
        // Backend mesaji kutsal: e-posta duplicate gibi mesajlar oldugu gibi gecer.
        // TODO(i18n)
        setAddUserError(responseErrorMessage(response, t, 'Kullanıcı eklenirken hata oluştu'))
      }
    } catch (error) {
      logError('UserManagement.handleAddUser', error)
      // TODO(i18n)
      setAddUserError(toUserMessage(error, t, 'Kullanıcı eklenirken hata oluştu'))
    } finally {
      setLoading(false)
      setIsSubmitting(false)
    }
  }

  // Edit user
  const handleEditUser = (user: User) => {
    setEditUserError(null)
    setEditingUser(user);
    setEditUser({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      role: user.role,
      company_id: user.company_id || user.company || 0
    });
    setShowEditModal(true);
  };

  const handleUpdateUser = async () => {
    if (isSubmitting) return

    // TODO(i18n)
    if (!editUser.first_name.trim() || !editUser.last_name.trim() || !editUser.email.trim()) {
      setEditUserError('Tüm alanlar gereklidir')
      return
    }

    if (editUser.company_id === 0) {
      setEditUserError('Şirket seçin')
      return
    }

    if (!editingUser) return

    try {
      setIsSubmitting(true)
      setLoading(true)
      setEditUserError(null)

      const updateDataWithDefaults = {
        ...editUser,
        is_active: true
      };
      const response = await userService.updateUser(editingUser.id, updateDataWithDefaults)

      if (response.success) {
        await loadUsers()
        setShowEditModal(false)
        setEditingUser(null)
      } else {
        // TODO(i18n)
        setEditUserError(responseErrorMessage(response, t, 'Kullanıcı güncellenirken hata oluştu'))
      }
    } catch (error) {
      logError('UserManagement.handleUpdateUser', error)
      // TODO(i18n)
      setEditUserError(toUserMessage(error, t, 'Kullanıcı güncellenirken hata oluştu'))
    } finally {
      setLoading(false)
      setIsSubmitting(false)
    }
  }

  // Delete user
  const handleDeleteUser = (user: User) => {
    setDeleteUserError(null)
    setDeletingUser(user)
    setShowDeleteModal(true)
  }

  const handleConfirmDelete = async () => {
    if (isSubmitting) return
    if (!deletingUser) return

    try {
      setIsSubmitting(true)
      setLoading(true)
      setDeleteUserError(null)

      const response = await userService.deleteUser(deletingUser.id)

      if (response.success) {
        await loadUsers()
        setShowDeleteModal(false)
        setDeletingUser(null)
      } else {
        // Yikici islem basarisiz: modal ACIK kalir, hata modal icinde gorunur.
        // TODO(i18n)
        setDeleteUserError(responseErrorMessage(response, t, 'Kullanıcı silinirken hata oluştu'))
      }
    } catch (error) {
      logError('UserManagement.handleConfirmDelete', error)
      // TODO(i18n)
      setDeleteUserError(toUserMessage(error, t, 'Kullanıcı silinirken hata oluştu'))
    } finally {
      setLoading(false)
      setIsSubmitting(false)
    }
  }

  // Change password
  const handleChangePassword = (user: User) => {
    setPasswordError(null)
    setPasswordUser(user)
    setNewPassword('')
    setConfirmPassword('')
    setShowPasswordModal(true)
  }

  const handleUpdatePassword = async () => {
    if (isSubmitting) return

    // TODO(i18n)
    if (!newPassword.trim()) {
      setPasswordError('Yeni şifre gereklidir')
      return
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Şifreler eşleşmiyor')
      return
    }

    if (newPassword.length < 6) {
      setPasswordError('Şifre en az 6 karakter olmalı')
      return
    }

    if (!passwordUser) return

    try {
      setIsSubmitting(true)
      setLoading(true)
      setPasswordError(null)

      const response = await userService.changePassword(passwordUser.id, newPassword)

      if (response.success) {
        setShowPasswordModal(false)
        setPasswordUser(null)
        setNewPassword('')
        setConfirmPassword('')
      } else {
        // TODO(i18n)
        setPasswordError(responseErrorMessage(response, t, 'Şifre değiştirilirken hata oluştu'))
      }
    } catch (error) {
      logError('UserManagement.handleUpdatePassword', error)
      // TODO(i18n)
      setPasswordError(toUserMessage(error, t, 'Şifre değiştirilirken hata oluştu'))
    } finally {
      setLoading(false)
      setIsSubmitting(false)
    }
  }

  // Filter users
  const filteredUsers = users.filter(user =>
    `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Load users and companies on mount
  useEffect(() => {
    loadCompanies()

    // Get current user from localStorage
    try {
      const storedUser = localStorage.getItem('user')
      if (storedUser) {
        setCurrentUser(JSON.parse(storedUser))
      }
    } catch (error) {
      // Bozuk kayit: eskiden bu JSON.parse korumasizdi ve effect icinde patlayip
      // ekrani komple dusuruyordu. Artik sessizce yutulmuyor, kullaniciya bildiriliyor.
      logError('UserManagement.readStoredUser', error)
      notifyError(t.errors.title, t.errors.sessionExpired)
    }

    // ESC ile dropdown kapatma. Modallarin ESC'ini ortak `Modal` bileseni yonetir
    // (istek ucustayken kapanmayi engeller), burada tekrar kapatilmaz.
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowRoleDropdown(false)
        setShowCompanyDropdown(false)
        setShowEditRoleDropdown(false)
        setShowEditCompanyDropdown(false)
      }
    }

    document.addEventListener('keydown', handleEscKey)
    return () => {
      document.removeEventListener('keydown', handleEscKey)
    }
  }, [])

  // Load users when currentUser is available
  useEffect(() => {
    if (currentUser) {
      loadUsers()
    }
  }, [currentUser])

  // Check if current user is admin or superadmin
  const isAdmin = currentUser?.role === 'admin'
  const isSuperAdmin = currentUser?.role === 'superadmin'
  const hasAdminAccess = isAdmin || isSuperAdmin
  const hasSuperAdminAccess = isSuperAdmin

  // Header'daki çıkış butonunun handler'ı (eski inline onClick gövdesi birebir)
  const handleLogout = async () => {
    try {
      await authService.logout()
      window.location.href = '/'
    } catch (error) {
      // Cikis her halukarda yapilir; kullanici zaten oturumu birakiyor.
      // Sadece kayit birakiyoruz, ayrica bir hata yuzeyi anlamsiz olurdu.
      logError('UserManagement.handleLogout', error)
      window.location.href = '/'
    }
  }

  return {
    // state
    companies,
    loading,
    isSubmitting,
    loadError,
    companiesError,
    addUserError,
    editUserError,
    deleteUserError,
    passwordError,
    searchTerm,
    setSearchTerm,
    currentUser,
    showAddModal,
    showEditModal,
    showDeleteModal,
    showPasswordModal,
    editingUser,
    deletingUser,
    passwordUser,
    showRoleDropdown,
    setShowRoleDropdown,
    showCompanyDropdown,
    setShowCompanyDropdown,
    showEditRoleDropdown,
    setShowEditRoleDropdown,
    showEditCompanyDropdown,
    setShowEditCompanyDropdown,
    newUser,
    setNewUser,
    editUser,
    setEditUser,
    newPassword,
    setNewPassword,
    confirmPassword,
    setConfirmPassword,
    // derived
    filteredUsers,
    hasAdminAccess,
    hasSuperAdminAccess,
    // actions
    openAddModal,
    closeAddModal,
    closeEditModal,
    closeDeleteModal,
    closePasswordModal,
    handleAddUser,
    handleEditUser,
    handleUpdateUser,
    handleDeleteUser,
    handleConfirmDelete,
    handleChangePassword,
    handleUpdatePassword,
    handleLogout,
    // error dismiss / retry
    dismissLoadError: () => setLoadError(null),
    dismissCompaniesError: () => setCompaniesError(null),
    dismissAddUserError: () => setAddUserError(null),
    dismissEditUserError: () => setEditUserError(null),
    dismissDeleteUserError: () => setDeleteUserError(null),
    dismissPasswordError: () => setPasswordError(null),
    retryLoadUsers: loadUsers,
    retryLoadCompanies: loadCompanies,
  }
}
